/**
 * Hooks for the voice API (`/api/voice`).
 *
 * `useVoiceConfig` answers "should we offer voice at all?" — the deployment may
 * have no ElevenLabs key, in which case the control is hidden rather than
 * offered and then failing on tap.
 *
 * `useVoiceTransport` returns the two authenticated calls the session makes.
 * Neither fits `useApi`: one is consumed by a raw `WebSocket` and the other
 * returns audio, not JSON.
 */
import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthToken } from "@/auth/AuthProvider";
import { useApi, type ApiError } from "@/lib/api";
import type { VoiceConfig } from "@/lib/types";

const voiceConfigKey = ["voice-config"] as const;

export function useVoiceConfig() {
  const apiCall = useApi();
  return useQuery<VoiceConfig, ApiError>({
    queryKey: voiceConfigKey,
    queryFn: ({ signal }) => apiCall<VoiceConfig>("/api/voice/config", { signal }),
    // Deployment-level configuration: it doesn't change while someone is
    // sitting in a car, and refetching it on every window focus would be noise.
    staleTime: Infinity,
  });
}

export interface VoiceTransport {
  /** Mint a fresh single-use Scribe WebSocket URL. Not reusable across reconnects. */
  mintSocketUrl: () => Promise<string>;
  /** Synthesize one reply. Rejects with a readable message; honours `signal` for barge-in. */
  fetchSpeech: (text: string, signal: AbortSignal) => Promise<Blob>;
}

export function useVoiceTransport(): VoiceTransport {
  const apiCall = useApi();
  const getToken = useAuthToken();

  const mintSocketUrl = useCallback(async () => {
    const { websocket_url } = await apiCall<{ websocket_url: string }>("/api/voice/stt-token", {
      method: "POST",
    });
    return websocket_url;
  }, [apiCall]);

  const fetchSpeech = useCallback(
    async (text: string, signal: AbortSignal): Promise<Blob> => {
      const token = await getToken();
      const response = await fetch("/api/voice/speech", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text }),
        signal,
      });
      if (!response.ok) {
        // The message is read aloud to the user as on-screen text, so it has to
        // be a sentence — not "API 503".
        throw new Error(
          response.status === 503
            ? "Voice playback is unavailable right now."
            : "Couldn't play the reply.",
        );
      }
      return response.blob();
    },
    [getToken],
  );

  return { mintSocketUrl, fetchSpeech };
}
